import {
  ActivityNode,
  ActivityPlanningActions,
  ActivityPlanningCards,
  ActivityPlanningContextModel,
  ActivityPlanningGovernance,
  ActivityPlanningScopeCode,
  ActivityPlanningSummary,
  ActivityPlanningTreeScopeModel,
  ActivityPlanningWorkbenchModel,
  BudgetLineRow,
  IndicatorCandidate,
  KpiPlanRow,
  PlanningTaskSaveRow,
  TaskRow,
} from './activityplanning.models';

const n = (value: any, fallback = 0) =>
  Number.isFinite(Number(value)) ? Number(value) : fallback;

const s = (value: any, fallback = '') =>
  value === null || value === undefined ? fallback : String(value);

const b = (value: any) =>
  value === true ||
  value === 1 ||
  value === '1' ||
  String(value).toLowerCase() === 'true';

export const formatNumber = (value: any) => n(value).toLocaleString();

export const formatMoney = (value: any) => {
  const valueNumber = n(value);
  const abs = Math.abs(valueNumber);

  if (abs >= 1_000_000_000_000)
    return `${(valueNumber / 1_000_000_000_000).toFixed(2)} T`;

  if (abs >= 1_000_000_000)
    return `${(valueNumber / 1_000_000_000).toFixed(2)} B`;

  if (abs >= 1_000_000) return `${(valueNumber / 1_000_000).toFixed(2)} M`;

  return valueNumber.toLocaleString();
};

export const formatPercent = (value: any) => {
  if (value === null || value === undefined || value === '') return '-';

  return `${n(value).toFixed(2)}%`;
};

export const normalizePlanningContext = (
  raw: any
): ActivityPlanningContextModel => ({
  planningcycleid: n(raw?.planningcycleid),
  userid: n(raw?.userid),
  profileid: raw?.profileid == null ? null : n(raw.profileid),
  profilestamp: raw?.profilestamp ?? null,

  cci: n(raw?.cci),
  ccc: s(raw?.ccc),
  ccn: s(raw?.ccn),

  costcenters: Array.isArray(raw?.costcenters)
    ? raw.costcenters.map((item: any) => ({
        staffuserid: n(item?.staffuserid),
        staffid: n(item?.staffid),
        staffcode: s(item?.staffcode),
        staffname: s(item?.staffname),
        unitid: n(item?.unitid),
        unitcode: s(item?.unitcode),
        unitname: s(item?.unitname),
        id: n(item?.id),
        code: s(item?.code),
        name: s(item?.name),
        primary: b(item?.primary),
        delegated: b(item?.delegated),
        typecode: s(item?.typecode),
        statecode: s(item?.statecode),
      }))
    : [],

  multiplecostcenters: b(raw?.multiplecostcenters),
  sudo: b(raw?.sudo),
});

export const normalizePlanningGovernance = (
  raw: any
): ActivityPlanningGovernance => ({
  activityplanningid: n(raw?.activityplanningid),
  planningcycleid: n(raw?.planningcycleid),
  costcenterid: n(raw?.costcenterid),

  stateid: n(raw?.stateid),
  statecode: s(raw?.statecode),
  statename: s(raw?.statename),

  completedat: raw?.completedat ?? null,
  completerid:
    raw?.completerid == null ? null : n(raw.completerid),

  authorizedat: raw?.authorizedat ?? null,
  authorizerid:
    raw?.authorizerid == null ? null : n(raw.authorizerid),

  activityplancount: n(raw?.activityplancount),
  readycount: n(raw?.readycount),
  plannedcount: n(raw?.plannedcount),
  validcount: n(raw?.validcount),
  invalidcount: n(raw?.invalidcount),
  issuecount: n(raw?.issuecount),
  blockingcount: n(raw?.blockingcount),
  plannedamount: n(raw?.plannedamount),

  canedit: b(raw?.canedit),
  cancomplete: b(raw?.cancomplete),

  activities: Array.isArray(raw?.activities)
    ? raw.activities
    : [],
});

export const normalizePlanningSummary = (
  raw: any
): ActivityPlanningSummary => ({
  indicatorcount: n(raw?.indicatorcount),
  activitycount: n(raw?.activitycount),

  readycount: n(raw?.readycount),
  plannedcount: n(raw?.plannedcount),
  validcount: n(raw?.validcount),
  invalidcount: n(raw?.invalidcount),
  issuecount: n(raw?.issuecount),
  blockingcount: n(raw?.blockingcount),

  taskcount: n(raw?.taskcount),
  budgetlinecount: n(raw?.budgetlinecount),
  budgetactivitycount: n(raw?.budgetactivitycount),
  plannedamount: n(raw?.plannedamount),
});

export const normalizePlanningActions = (
  raw: any
): ActivityPlanningActions => ({
  canRefresh: b(raw?.canRefresh),
  canChangeCostCenter: b(raw?.canChangeCostCenter),

  canEdit: b(raw?.canEdit),

  canAddIndicator: b(raw?.canAddIndicator),
  canAddActivity: b(raw?.canAddActivity),
  canAddTask: b(raw?.canAddTask),
  canApplyTemplate: b(raw?.canApplyTemplate),

  canDeclareReady: b(raw?.canDeclareReady),
  canComplete: b(raw?.canComplete),

  canExit: b(raw?.canExit),
  nextpage: raw?.nextpage ?? null,
});

export const normalizeActivityPlanningWorkbench = (
  raw: any
): ActivityPlanningWorkbenchModel => {
  const source = raw?.sd ?? raw ?? {};

  return {
    context: normalizePlanningContext(source.context),
    planning: normalizePlanningGovernance(source.planning),
    summary: normalizePlanningSummary(source.summary),
    tree: Array.isArray(source.tree) ? source.tree : [],
    register: Array.isArray(source.register)
      ? source.register
      : [],
    actions: normalizePlanningActions(source.actions),
  };
};

// export const normalizePlanningSummary = (
//   raw: any
// ): ActivityPlanningSummary => ({
//   indicatorcount: n(raw?.indicatorcount),
//   activitycount: n(raw?.activitycount),
//   taskcount: n(raw?.taskcount),
//   budgetlinecount: n(raw?.budgetlinecount),
//   budgetactivitycount: n(raw?.budgetactivitycount),
//   plannedamount: n(raw?.plannedamount),
// });

// export const normalizePlanningActions = (
//   raw: any
// ): ActivityPlanningActions => ({
//   canRefresh: b(raw?.canRefresh),
//   canChangeCostCenter: b(raw?.canChangeCostCenter),
//   canAddIndicator: b(raw?.canAddIndicator),
//   canAddActivity: b(raw?.canAddActivity),
//   canAddTask: b(raw?.canAddTask),
//   canApplyTemplate: b(raw?.canApplyTemplate),
//   canSubmit: b(raw?.canSubmit),
//   canExit: b(raw?.canExit),
//   nextpage: raw?.nextpage ?? null,
// });

// export const normalizeActivityPlanningWorkbench = (
//   raw: any
// ): ActivityPlanningWorkbenchModel => {
//   const source = raw?.sd ?? raw ?? {};

//   return {
//     context: normalizePlanningContext(source.context),
//     summary: normalizePlanningSummary(source.summary),
//     tree: Array.isArray(source.tree) ? source.tree : [],
//     register: Array.isArray(source.register) ? source.register : [],
//     actions: normalizePlanningActions(source.actions),
//   };
// };

export const normalizeIndicatorCandidate = (raw: any): IndicatorCandidate => ({
  rid: n(raw?.rid),
  rix: s(raw?.rix) || undefined,

  kpiid: n(raw?.kpiid ?? raw?.rid),
  kpiix: s(raw?.kpiix ?? raw?.rix) || undefined,
  kpiplanid: raw?.kpiplanid == null ? null : n(raw.kpiplanid),
  kpiplanix: raw?.kpiplanix == null ? null : s(raw.kpiplanix),
  planned: b(raw?.planned),

  shc: s(raw?.shc),
  nam: s(raw?.nam),
  dsc: s(raw?.dsc) || undefined,

  typeid: raw?.typeid == null ? null : n(raw.typeid),
  frequencyid: raw?.frequencyid == null ? null : n(raw.frequencyid),
  frequencycode: s(raw?.frequencycode) || undefined,
  frequencyname: s(raw?.frequencyname) || undefined,

  uomid: raw?.uomid == null ? null : n(raw.uomid),
  uomcode: s(raw?.uomcode) || undefined,
  uomname: s(raw?.uomname) || undefined,
  uomsymbol: s(raw?.uomsymbol) || undefined,
  uomminimum: raw?.uomminimum == null ? null : n(raw.uomminimum),
  uommaximum: raw?.uommaximum == null ? null : n(raw.uommaximum),
  uomstep: raw?.uomstep == null ? null : n(raw.uomstep),
  uombounded: b(raw?.uombounded),

  targetdescription:
    raw?.targetdescription == null ? null : s(raw.targetdescription),
  recommendedbaseline:
    raw?.recommendedbaseline == null ? null : n(raw.recommendedbaseline),
  recommendedwarninglow:
    raw?.recommendedwarninglow == null ? null : n(raw.recommendedwarninglow),
  recommendedwarninghigh:
    raw?.recommendedwarninghigh == null ? null : n(raw.recommendedwarninghigh),
  suggestedtarget: raw?.suggestedtarget == null ? null : n(raw.suggestedtarget),

  objectiveid: raw?.objectiveid == null ? null : n(raw.objectiveid),
  objectivecode: s(raw?.objectivecode) || undefined,
  objectivename: s(raw?.objectivename) || undefined,
  globalobjectiveid:
    raw?.globalobjectiveid == null ? null : n(raw.globalobjectiveid),
  globalobjectivecode: s(raw?.globalobjectivecode) || undefined,
  globalobjectivename: s(raw?.globalobjectivename) || undefined,
  axisid: raw?.axisid == null ? null : n(raw.axisid),
  axiscode: s(raw?.axiscode) || undefined,
  axisname: s(raw?.axisname) || undefined,

  costcenterid: raw?.costcenterid == null ? null : n(raw.costcenterid),
  periodid: raw?.periodid == null ? null : n(raw.periodid),
  cycleid: raw?.cycleid == null ? null : n(raw.cycleid),
  stateid: raw?.stateid == null ? null : n(raw.stateid),
  plantypeid: raw?.plantypeid == null ? null : n(raw.plantypeid),

  formula: s(raw?.formula) || undefined,
  interpretation: s(raw?.interpretation) || undefined,
});

export const normalizeKpiPlanRows = (model: { tree: any[] }): KpiPlanRow[] => {
  const rows = new Map<number, KpiPlanRow>();

  for (const raw of model.tree ?? []) {
    const kpiplanid = n(raw?.kpiplanid);

    if (!kpiplanid || rows.has(kpiplanid)) continue;

    rows.set(kpiplanid, {
      id: kpiplanid,
      kpiplanid,
      kpiplanix: s(raw?.kpiplanix),
      kpiid: n(raw?.kpiid),

      kpicode: s(raw?.kpicode),
      kpiname: s(raw?.kpiname),
      kpidescription: s(raw?.kpidescription),

      uomcode: s(raw?.uomcode),
      uomname: s(raw?.uomname),
      uomsymbol: s(raw?.uomsymbol),
      frequencycode: s(raw?.frequencycode),
      frequencyname: s(raw?.frequencyname),

      axiscode: s(raw?.axiscode),
      axisname: s(raw?.axisname),
      objectivecode: s(raw?.objectivecode),
      objectivename: s(raw?.objectivename),

      baselinevalue: n(raw?.baselinevalue),
      targetvalue: n(raw?.targetvalue),
      thresholdmin: raw?.thresholdmin == null ? null : n(raw.thresholdmin),
      thresholdmax: raw?.thresholdmax == null ? null : n(raw.thresholdmax),
      description: s(raw?.kpiplandescription),

      formula: s(raw?.formula),
      interpretation: s(raw?.interpretation),

      statecode: s(raw?.statecode, 'DRAFT'),
      statename: s(raw?.statename, 'Draft'),
      stamp: s(raw?.kpiplanstamp),

      _state: 'clean',
      __dirty: false,
      __error: '',
      raw,
    });
  }

  return Array.from(rows.values());
};

export const normalizePlanningTree = (
  rows: any[],
  context?: ActivityPlanningContextModel
): ActivityNode[] => {
  const output: ActivityNode[] = [];
  const indicators = new Set<string>();
  const activities = new Set<string>();
  const tasks = new Set<string>();

  for (const row of rows ?? []) {
    const kpiplanid = n(row?.kpiplanid);
    const activityplanid = n(row?.activityplanid);
    const taskplanid = n(row?.taskplanid);

    const kpiNid = `KPI-${kpiplanid}`;
    const activityNid = `ACTIVITY-${activityplanid}`;
    const taskNid = `TASK-${taskplanid}`;

    if (kpiplanid && !indicators.has(kpiNid)) {
      indicators.add(kpiNid);

      output.push({
        nid: kpiNid,
        pid: null,
        rid: kpiplanid,
        kpiplanid,
        kpiid: n(row?.kpiid),

        code: s(row?.kpicode),
        shortcode: s(row?.kpicode),
        name: s(row?.kpiname),
        recname: s(row?.kpiname),
        description: s(row?.kpiplandescription || row?.kpidescription),

        nodetype: 'KPI',
        levelno: 1,

        axiscode: s(row?.axiscode),
        axisname: s(row?.axisname),
        objectivecode: s(row?.objectivecode),
        objectivename: s(row?.objectivename),

        costcenterid: n(row?.costcenterid),
        costcentercode: s(row?.costcentercode || context?.ccc),
        costcentername: s(row?.costcentername || context?.ccn),

        status: s(row?.statecode, 'DRAFT'),
        totalamount: n(row?.activityplannedamount),
        raw: row,
      });
    }

    if (activityplanid && !activities.has(activityNid)) {
      activities.add(activityNid);

      output.push({
        nid: activityNid,
        pid: kpiNid,
        rid: activityplanid,
        prid: kpiplanid,

        kpiplanid,
        kpiid: n(row?.kpiid),

        activityplanid,
        activityid: n(row?.activityid),

        code: s(row?.activitycode),
        shortcode: s(row?.activitycode),
        name: s(row?.activityname),
        recname: s(row?.activityname),

        nodetype: 'ACTIVITY',
        levelno: 2,

        axiscode: s(row?.axiscode),
        axisname: s(row?.axisname),
        objectivecode: s(row?.objectivecode),
        objectivename: s(row?.objectivename),

        costcenterid: n(row?.costcenterid),
        costcentercode: s(row?.costcentercode || context?.ccc),
        costcentername: s(row?.costcentername || context?.ccn),

        contributionweight: n(row?.contributionweight),
        status: s(row?.statecode),
        totalamount: n(row?.activityplannedamount),
        budgetlinecount: n(row?.budgetlinecount),
        budgetstate: n(row?.activitybudgetstate),
        raw: row,
      });
    }

    if (taskplanid && !tasks.has(taskNid)) {
      tasks.add(taskNid);

      output.push({
        nid: taskNid,
        pid: activityNid,
        rid: taskplanid,
        prid: activityplanid,

        kpiplanid,
        kpiid: n(row?.kpiid),

        activityplanid,
        activityid: n(row?.activityid),

        taskplanid,
        taskid: n(row?.taskid),

        code: s(row?.taskcode),
        shortcode: s(row?.taskcode),
        name: s(row?.taskname),
        recname: s(row?.taskname),

        nodetype: 'TASK',
        levelno: 3,

        axiscode: s(row?.axiscode),
        axisname: s(row?.axisname),
        objectivecode: s(row?.objectivecode),
        objectivename: s(row?.objectivename),

        costcenterid: n(row?.costcenterid),
        costcentercode: s(row?.costcentercode || context?.ccc),
        costcentername: s(row?.costcentername || context?.ccn),

        taskweight: n(row?.taskweight),
        status: s(row?.statecode),
        totalamount: n(row?.taskplannedamount),
        budgetlinecount: n(row?.budgetlinecount),
        budgetstate: n(row?.taskbudgetstate),
        raw: row,
      });
    }
  }

  return output;
};

export const normalizeActivityPlanningTreeScope0 = (
  value: any,
  context?: ActivityPlanningContextModel
): ActivityPlanningTreeScopeModel => {
  const source = value ?? {};

  return {
    scope: s(source.scope, 'OVERVIEW') as ActivityPlanningScopeCode,
    canplan: Boolean(source.canplan),

    kpiplancount: n(source.kpiplancount),
    activityplancount: n(source.activityplancount),
    taskplancount: n(source.taskplancount),

    tree: normalizePlanningTree(
      Array.isArray(source.tree) ? source.tree : [],
      context
    ),
  };
};

export const normalizeActivityPlanningTreeScope = (
  value: any,
  context?: ActivityPlanningContextModel
): ActivityPlanningTreeScopeModel => {
  const source = value ?? {};

  return {
    scope: s(source.scope, 'OVERVIEW') as ActivityPlanningScopeCode,
    canplan: Boolean(source.canplan),

    kpiplancount: n(source.kpiplancount),
    activityplancount: n(source.activityplancount),
    taskplancount: n(source.taskplancount),

    tree: normalizePlanningTree(
      Array.isArray(source.tree) ? source.tree : [],
      context
    ),
  };
};

export const summaryCards = (
  model?: ActivityPlanningWorkbenchModel,
  selected?: ActivityNode | null
): ActivityPlanningCards | null => {
  if (!model) return null;

  return {
    activitytotal: model.summary.activitycount,
    tasktotal: model.summary.taskcount,
    budgettotal: n(model.summary.plannedamount),
    variance: 0,
    weightedprogress: 0,
    taskcount: model.summary.taskcount,
    budgetlinecount: model.summary.budgetlinecount,
  };
};

export const normalizeTask = (row: any, index: number = 0): TaskRow => {
  const taskid = row.taskid ?? row.tid ?? row.rid ?? null;
  const taskplanid = row.taskplanid ?? row.tpi ?? null;

  return {
    /*
     * Client/grid identity:
     *
     * Prefer TaskPlan when seasonal,
     * otherwise Task Master.
     */
    id: taskplanid ?? taskid ?? `task-${index}`,

    /*
     * Master identity.
     */
    taskid: taskid == null ? 0 : Number(taskid),

    /*
     * Seasonal identity.
     */
    taskplanid: taskplanid == null ? null : Number(taskplanid),

    activityplanid:
      row.activityplanid == null ? null : Number(row.activityplanid),
    code: String(row.code ?? row.shortcode ?? row.shc ?? row.taskcode ?? ''),
    name: String(row.name ?? row.recname ?? row.nam ?? row.taskname ?? ''),
    description: String(
      row.description ?? row.dsc ?? row.taskdescription ?? ''
    ),
    weight: Number(row.weight ?? row.wgt ?? 0),
    plannedamount: Number(row.plannedamount ?? row.amount ?? row.amt ?? 0),

    plannedstartdate:
      row.plannedstartdate == null ? null : String(row.plannedstartdate),

    plannedenddate:
      row.plannedenddate == null ? null : String(row.plannedenddate),

    planned: row.planned != null ? Boolean(row.planned) : taskplanid != null,
    budgetlinecount: Number(row.budgetlinecount ?? 0),
    budgetstate: Number(row.budgetstate ?? 0),
    status: String(row.status ?? row.sts ?? 1),
    raw: row,
  };
};

export const normalizeTaskSave = (row: PlanningTaskSaveRow) => ({
  taskid: row.taskid != null ? Number(row.taskid) : null,
  recname: String(row.name ?? '').trim(),
  description: String(row.description ?? '').trim(),
  weight: Number(row.weight ?? 1),
  plannedstartdate: row.plannedstartdate ?? null,
  plannedenddate: row.plannedenddate ?? null,
  ownerid: row.ownerid ?? null,
  targetvalue: Number(row.targetvalue ?? 0),
  remarks: String(row.remarks ?? '').trim(),
});

export const normalizeBudgetLine = (
  row: any,
  taskplanid: number,
  index: number = 0
): BudgetLineRow => ({
  id: row.rid ?? `budget-${taskplanid}-${index}`,
  rid: row.rid == null ? null : Number(row.rid),
  budgetlineid: row.rid == null ? null : Number(row.rid),
  taskplanid: Number(row.taskplanid ?? taskplanid),
  taskid: row.taskid == null ? null : Number(row.taskid),
  accountid: row.accountid == null ? null : Number(row.accountid),
  accountcode: String(row.accountcode ?? ''),
  accountname: String(row.accountname ?? ''),
  name: String(row.name ?? ''),
  shortcode: String(row.shortcode ?? ''),
  description: String(row.description ?? ''),
  recno: Number(row.recno ?? index + 1),
  quantity: Number(row.quantity ?? 0),
  unitprice: Number(row.unitprice ?? 0),
  amount: Number(row.amount ?? 0),
  remarks: String(row.remarks ?? ''),
  stamp: String(row.stamp ?? ''),
  status: Number(row.status ?? 1),
  _state: 'clean',
  raw: row,
});

// export const normalizeBudgetLine1 = (
//   row: any
// ): BudgetLineRow => ({
//   id:
//     row.id ??
//     row.rid ??
//     row.recid,

//   rid:
//     Number(
//       row.rid ??
//       row.recid ??
//       0
//     ) || null,

//   taskplanid:
//     Number(
//       row.taskplanid ??
//       row.tpi ??
//       0
//     ),

//   activityid:
//     Number(
//       row.activityid ??
//       row.aid ??
//       0
//     ),

//   taskid:
//     Number(
//       row.taskid ??
//       row.tid ??
//       0
//     ),

//   accountid:
//     row.accountid != null
//       ? Number(row.accountid)
//       : row.aci != null
//         ? Number(row.aci)
//         : null,

//   accountcode:
//     row.accountcode ??
//     row.acc ??
//     '',

//   accountname:
//     row.accountname ??
//     row.acn ??
//     '',

//   name:
//     row.name ??
//     row.nam ??
//     '',

//   shortcode:
//     row.shortcode ??
//     row.shc ??
//     '',

//   description:
//     row.description ??
//     row.dsc ??
//     '',

//   quantity:
//     Number(
//       row.quantity ??
//       row.qty ??
//       0
//     ),

//   unitprice:
//     Number(
//       row.unitprice ??
//       row.upr ??
//       0
//     ),

//   amount:
//     Number(
//       row.amount ??
//       row.amt ??
//       0
//     ),

//   remarks:
//     row.remarks ??
//     row.rmk ??
//     '',

//   recno:
//     Number(
//       row.recno ??
//       row.rno ??
//       0
//     ),

//   status:
//     Number(
//       row.status ??
//       row.sts ??
//       1
//     ),

//   stamp:
//     row.stamp ??
//     row.stp ??
//     '',

//   _state: 'clean',

//   raw: row,
// });
